import React, { useEffect } from 'react';
import type { RecurrencePattern, RecurrenceFrequency, RecurrenceMonthMode, RecurrenceEndMode } from '../types/rostering';

interface PeriodicitySettingsProps {
  value: RecurrencePattern;
  onChange: (value: RecurrencePattern) => void;
  includeStartDate?: boolean;
  includeEndDateOption?: boolean;
}

const weekDays = [
  { label: 'Mo', value: 1 },
  { label: 'Tu', value: 2 },
  { label: 'We', value: 3 },
  { label: 'Th', value: 4 },
  { label: 'Fr', value: 5 },
  { label: 'Sa', value: 6 },
  { label: 'Su', value: 0 },
];

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const weekPositions = [
  { label: 'first', value: 1 },
  { label: 'second', value: 2 },
  { label: 'third', value: 3 },
  { label: 'fourth', value: 4 },
  { label: 'last', value: 5 },
];

const weekDayNames = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
];

const PeriodicitySettings: React.FC<PeriodicitySettingsProps> = ({
  value,
  onChange,
  includeStartDate = true,
  includeEndDateOption = true,
}) => {
  const update = <K extends keyof RecurrencePattern>(field: K, fieldValue: RecurrencePattern[K]) => {
    onChange({ ...value, [field]: fieldValue });
  };

  useEffect(() => {
    if (!includeEndDateOption && value.endMode === 'on') {
      onChange({ ...value, endMode: 'never' });
    }
  }, [includeEndDateOption, onChange, value]);

  const updateFrequency = (frequency: RecurrenceFrequency) => {
    onChange({
      ...value,
      frequency,
      weeklyDays: frequency === 'weekly' && value.weeklyDays.length === 0 ? [1] : value.weeklyDays,
    });
  };

  const toggleWeekDay = (day: number) => {
    const exists = value.weeklyDays.includes(day);
    const next = exists ? value.weeklyDays.filter(d => d !== day) : [...value.weeklyDays, day];
    update('weeklyDays', next.sort((a, b) => a - b));
  };

  return (
    <div className="card bg-base-100 border border-base-300">
      <div className="card-body p-4 space-y-4">
        <h3 className="font-semibold text-base-content">Periodicity</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {includeStartDate && (
            <div className="form-control">
              <label className="label"><span className="label-text">Start Date</span></label>
              <input
                type="date"
                className="input input-bordered"
                value={value.startDate}
                onChange={(e) => update('startDate', e.target.value)}
              />
            </div>
          )}

          <div className="form-control">
            <label className="label"><span className="label-text">Repeat</span></label>
            <select
              className="select select-bordered"
              value={value.frequency}
              onChange={(e) => updateFrequency(e.target.value as RecurrenceFrequency)}
            >
              <option value="never">Never</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        </div>

        {value.frequency !== 'never' && (
          <>
            <div className="form-control max-w-xs">
              <label className="label"><span className="label-text">Repeat every</span></label>
              <div className="join">
                <input
                  type="number"
                  min={1}
                  className="input input-bordered join-item w-24"
                  value={value.interval}
                  onChange={(e) => update('interval', Math.max(1, parseInt(e.target.value || '1', 10)))}
                />
                <button type="button" className="btn btn-disabled join-item">
                  {value.frequency === 'daily' && 'day(s)'}
                  {value.frequency === 'weekly' && 'week(s)'}
                  {value.frequency === 'monthly' && 'month(s)'}
                  {value.frequency === 'yearly' && 'year(s)'}
                </button>
              </div>
            </div>

            {value.frequency === 'weekly' && (
              <div className="form-control">
                <label className="label"><span className="label-text">Repeat on</span></label>
                <div className="flex flex-wrap gap-3">
                  {weekDays.map((d) => (
                    <label key={d.value} className="label cursor-pointer gap-2 p-0">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={value.weeklyDays.includes(d.value)}
                        onChange={() => toggleWeekDay(d.value)}
                      />
                      <span className="label-text">{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {value.frequency === 'monthly' && (
              <div className="space-y-3">
                <label className="label py-0"><span className="label-text">Repeat on</span></label>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="label cursor-pointer gap-2 p-0">
                    <input
                      type="radio"
                      className="radio radio-sm"
                      checked={value.monthMode === 'day_of_month'}
                      onChange={() => update('monthMode', 'day_of_month' as RecurrenceMonthMode)}
                    />
                    <span className="label-text">Day</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    className="input input-bordered w-24"
                    value={value.monthDay}
                    onChange={(e) => update('monthDay', Math.min(31, Math.max(1, parseInt(e.target.value || '1', 10))))}
                    disabled={value.monthMode !== 'day_of_month'}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="label cursor-pointer gap-2 p-0">
                    <input
                      type="radio"
                      className="radio radio-sm"
                      checked={value.monthMode === 'weekday_of_month'}
                      onChange={() => update('monthMode', 'weekday_of_month' as RecurrenceMonthMode)}
                    />
                    <span className="label-text">The</span>
                  </label>
                  <select
                    className="select select-bordered"
                    value={value.monthWeek}
                    onChange={(e) => update('monthWeek', parseInt(e.target.value, 10))}
                    disabled={value.monthMode !== 'weekday_of_month'}
                  >
                    {weekPositions.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                  <select
                    className="select select-bordered"
                    value={value.monthWeekday}
                    onChange={(e) => update('monthWeekday', parseInt(e.target.value, 10))}
                    disabled={value.monthMode !== 'weekday_of_month'}
                  >
                    {weekDayNames.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>
            )}

            {value.frequency === 'yearly' && (
              <div className="space-y-3">
                <label className="label py-0"><span className="label-text">Repeat on</span></label>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="label cursor-pointer gap-2 p-0">
                    <input
                      type="radio"
                      className="radio radio-sm"
                      checked={value.yearMode === 'day_of_month'}
                      onChange={() => update('yearMode', 'day_of_month' as RecurrenceMonthMode)}
                    />
                  </label>
                  <select
                    className="select select-bordered"
                    value={value.yearMonth}
                    onChange={(e) => update('yearMonth', parseInt(e.target.value, 10))}
                  >
                    {months.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
                  </select>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    className="input input-bordered w-24"
                    value={value.yearDay}
                    onChange={(e) => update('yearDay', Math.min(31, Math.max(1, parseInt(e.target.value || '1', 10))))}
                    disabled={value.yearMode !== 'day_of_month'}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="label cursor-pointer gap-2 p-0">
                    <input
                      type="radio"
                      className="radio radio-sm"
                      checked={value.yearMode === 'weekday_of_month'}
                      onChange={() => update('yearMode', 'weekday_of_month' as RecurrenceMonthMode)}
                    />
                  </label>
                  <select
                    className="select select-bordered"
                    value={value.yearWeek}
                    onChange={(e) => update('yearWeek', parseInt(e.target.value, 10))}
                    disabled={value.yearMode !== 'weekday_of_month'}
                  >
                    {weekPositions.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                  <select
                    className="select select-bordered"
                    value={value.yearWeekday}
                    onChange={(e) => update('yearWeekday', parseInt(e.target.value, 10))}
                    disabled={value.yearMode !== 'weekday_of_month'}
                  >
                    {weekDayNames.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                  <span className="text-sm opacity-70">of</span>
                  <select
                    className="select select-bordered"
                    value={value.yearMonth}
                    onChange={(e) => update('yearMonth', parseInt(e.target.value, 10))}
                    disabled={value.yearMode !== 'weekday_of_month'}
                  >
                    {months.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="label py-0"><span className="label-text">End</span></label>
              <div className="flex flex-wrap items-center gap-4">
                <label className="label cursor-pointer gap-2 p-0">
                  <input
                    type="radio"
                    className="radio radio-sm"
                    checked={value.endMode === 'never'}
                    onChange={() => update('endMode', 'never' as RecurrenceEndMode)}
                  />
                  <span className="label-text">Never</span>
                </label>

                <label className="label cursor-pointer gap-2 p-0">
                  <input
                    type="radio"
                    className="radio radio-sm"
                    checked={value.endMode === 'after'}
                    onChange={() => update('endMode', 'after' as RecurrenceEndMode)}
                  />
                  <span className="label-text">After</span>
                </label>
                <input
                  type="number"
                  min={1}
                  className="input input-bordered w-24"
                  value={value.occurrences}
                  onChange={(e) => update('occurrences', Math.max(1, parseInt(e.target.value || '1', 10)))}
                  disabled={value.endMode !== 'after'}
                />
                <span className="text-sm">occurrence(s)</span>
              </div>

              {includeEndDateOption && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="label cursor-pointer gap-2 p-0">
                    <input
                      type="radio"
                      className="radio radio-sm"
                      checked={value.endMode === 'on'}
                      onChange={() => update('endMode', 'on' as RecurrenceEndMode)}
                    />
                    <span className="label-text">On</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered"
                    value={value.endDate}
                    onChange={(e) => update('endDate', e.target.value)}
                    disabled={value.endMode !== 'on'}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PeriodicitySettings;
